/**
 * Product Card Utilities
 * Shared functionality for product cards with video/image hover effects
 */

class ProductCardManager {
    constructor() {
      this.cards = [];
      this.isMobile = window.innerWidth < 750;
      this.cardObserver = null;
      this.initialized = false;
      this.resizeTimeout = null;
      this.currentlyPlayingCard = null; // Track the currently playing card
    }
  
    /**
     * Initialize product cards on the page
     * @param {string} cardSelector - CSS selector for card elements
     */
    init(cardSelector = '[data-product-card]') {
      if (this.initialized) return;
  
      // Set up resize handler
      window.addEventListener('resize', () => this.handleResize());
  
      // Find all product cards
      const productCards = document.querySelectorAll(cardSelector);
      if (productCards.length === 0) return;
  
      // Set up IntersectionObserver for visibility detection
      this.cardObserver = new IntersectionObserver(
        (entries) => this.handleCardVisibility(entries), 
        { threshold: 0.8 } // Card is considered visible when 40% is in view
      );
  
      // Initialize each card
      productCards.forEach(card => {
        this.initCard(card);
      });
  
      this.initialized = true;
    }
  
    /**
     * Initialize a single product card
     * @param {HTMLElement} card - The card element
     */
    initCard(card) {
      // Add index attribute for determining first visible card
      card.setAttribute('data-card-index', this.cards.length);
      
      // Find and cache media elements
      card.mediaContainer = card.querySelector('.media.media--hover-effect');
      card.primaryImage = card.mediaContainer ? card.mediaContainer.querySelector('img:first-child') : null;
      card.secondaryMedia = card.mediaContainer ? (
        card.mediaContainer.querySelector('img:not(:first-child)') || 
        card.mediaContainer.querySelector('.video-container')
      ) : null;
      card.videoElement = card.querySelector('video[data-autoplay-on-visible], video[data-autoplay-on-hover]');
      card.youtubeIframe = card.querySelector('iframe.js-youtube');
      card.vimeoIframe = card.querySelector('iframe.js-vimeo');
      
      // Flag to determine if this card has media
      card.hasMedia = !!(card.videoElement || card.youtubeIframe || card.vimeoIframe);
      card.hasSecondaryMedia = !!card.secondaryMedia;
  
      // Store in cards array
      this.cards.push(card);
  
      // Set up hover interactions
      this.setupCardInteractions(card);
  
      // Start observing visibility
      this.cardObserver.observe(card);
    }
  
    /**
     * Handle resize events
     */
    handleResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 990;
        
        // If switching between mobile and desktop, reset all cards
        if (wasMobile !== this.isMobile) {
          this.resetAllCards();
          this.currentlyPlayingCard = null;
        }
        
        this.updateCardBehavior();
      }, 250);
    }
  
    /**
     * Reset all cards to their default state
     */
    resetAllCards() {
      this.cards.forEach(card => {
        // Reset media playback
        this.pauseCardMedia(card);
        
        // Reset CSS for secondary media visibility
        if (card.hasSecondaryMedia && card.primaryImage && card.secondaryMedia) {
          card.primaryImage.style.opacity = '1';
          card.secondaryMedia.style.opacity = '0';
        }
      });
    }
  
    /**
     * Update card behavior based on current viewport size
     */
    updateCardBehavior() {
      this.cards.forEach(card => {
        // Update hover behavior based on current viewport size
        this.setupCardInteractions(card);
      });
      
      // Re-evaluate visible cards to show only the first one
      if (this.isMobile) {
        this.updateVisibleCards();
      }
    }
    
    /**
     * Update which cards are visible and play only the first visible one with media
     */
    updateVisibleCards() {
      if (!this.isMobile) return;
      
      // First, hide secondary media on all cards
      this.cards.forEach(card => {
        if (card.hasSecondaryMedia && card !== this.currentlyPlayingCard) {
          this.hideSecondaryMedia(card);
        }
      });
      
      // Get all visible cards
      const visibleCards = this.cards.filter(card => card.classList.contains('is-visible') && card.hasMedia);
      
      // Sort by DOM order (data-card-index)
      visibleCards.sort((a, b) => {
        return parseInt(a.getAttribute('data-card-index')) - parseInt(b.getAttribute('data-card-index'));
      });
      
      // If we have visible cards with media, play only the first one
      if (visibleCards.length > 0) {
        const firstVisibleCard = visibleCards[0];
        
        // Only play if it's not already playing
        if (this.currentlyPlayingCard !== firstVisibleCard) {
          // Pause all media first
          if (this.currentlyPlayingCard) {
            this.pauseCardMedia(this.currentlyPlayingCard);
            this.hideSecondaryMedia(this.currentlyPlayingCard);
          }
          
          // Then play the first visible card
          this.showSecondaryMedia(firstVisibleCard);
          this.playCardMedia(firstVisibleCard);
          this.currentlyPlayingCard = firstVisibleCard;
        }
      } else {
        // No visible cards with media
        this.resetAllCards();
        this.currentlyPlayingCard = null;
      }
    }
  
    /**
     * Show secondary media (second image or video container)
     * @param {HTMLElement} card - The card element
     */
    showSecondaryMedia(card) {
      if (card.hasSecondaryMedia && card.primaryImage && card.secondaryMedia) {
        card.primaryImage.style.opacity = '0';
        card.secondaryMedia.style.opacity = '1';
      }
    }
  
    /**
     * Hide secondary media and show primary image
     * @param {HTMLElement} card - The card element
     */
    hideSecondaryMedia(card) {
      if (card.hasSecondaryMedia && card.primaryImage && card.secondaryMedia) {
        card.primaryImage.style.opacity = '1';
        card.secondaryMedia.style.opacity = '0';
      }
    }
  
    /**
     * Handle card visibility changes
     * @param {IntersectionObserverEntry[]} entries - Intersection observer entries
     */
    handleCardVisibility(entries) {
      let visibilityChanged = false;
      
      entries.forEach(entry => {
        const card = entry.target;
        const wasVisible = card.classList.contains('is-visible');
        
        if (entry.isIntersecting) {
          card.classList.add('is-visible');
          if (!wasVisible) visibilityChanged = true;
        } else {
          card.classList.remove('is-visible');
          if (wasVisible) {
            visibilityChanged = true;
            
            // If this was the playing card, reset it
            if (card === this.currentlyPlayingCard) {
              this.pauseCardMedia(card);
              this.hideSecondaryMedia(card);
              this.currentlyPlayingCard = null;
            }
          }
        }
      });
      
      // If visibility changed and we're on mobile, update playing state
      if (visibilityChanged && this.isMobile) {
        this.updateVisibleCards();
      }
    }
  
    /**
     * Set up event listeners for card interactions
     * @param {HTMLElement} card - The card element
     */
    setupCardInteractions(card) {
      // Remove existing event listeners
      card.removeEventListener('mouseenter', card._onMouseEnter);
      card.removeEventListener('mouseleave', card._onMouseLeave);
  
      // Only apply hover events on desktop
      if (!this.isMobile) {
        // Store the handler references so they can be removed later
        card._onMouseEnter = () => {
          // Reset any currently playing card
          if (this.currentlyPlayingCard && this.currentlyPlayingCard !== card) {
            this.pauseCardMedia(this.currentlyPlayingCard);
            this.hideSecondaryMedia(this.currentlyPlayingCard);
          }
          
          // Show this card's secondary media and play it
          this.showSecondaryMedia(card);
          this.playCardMedia(card);
          this.currentlyPlayingCard = card;
        };
        
        card._onMouseLeave = () => {
          this.pauseCardMedia(card);
          this.hideSecondaryMedia(card);
          this.currentlyPlayingCard = null;
        };
  
        card.addEventListener('mouseenter', card._onMouseEnter);
        card.addEventListener('mouseleave', card._onMouseLeave);
      }
    }
  
    /**
     * Play card media (video or iframe)
     * @param {HTMLElement} card - The card element
     */
    playCardMedia(card) {
      if (!card.hasMedia) return;
      
      // Play video if it exists
      if (card.videoElement) {
        card.videoElement.play().catch(e => {
          // Handle autoplay restrictions gracefully
          console.warn('Video autoplay was prevented:', e);
        });
      }
  
      // Load iframe if it exists
      if (card.youtubeIframe) {
        const src = card.youtubeIframe.getAttribute('data-src');
        if (src && card.youtubeIframe.getAttribute('src') !== src) {
          card.youtubeIframe.setAttribute('src', src);
        }
      } else if (card.vimeoIframe) {
        const src = card.vimeoIframe.getAttribute('data-src');
        if (src && card.vimeoIframe.getAttribute('src') !== src) {
          card.vimeoIframe.setAttribute('src', src);
        }
      }
    }
  
    /**
     * Pause card media (video or iframe)
     * @param {HTMLElement} card - The card element
     */
    pauseCardMedia(card) {
      if (!card || !card.hasMedia) return;
      
      // Pause video if it exists
      if (card.videoElement) {
        card.videoElement.pause();
        // Reset video to beginning
        card.videoElement.currentTime = 0;
      }
  
      // Unload iframe if it exists
      if (card.youtubeIframe && card.youtubeIframe.getAttribute('src')) {
        card.youtubeIframe.setAttribute('src', '');
      } else if (card.vimeoIframe && card.vimeoIframe.getAttribute('src')) {
        card.vimeoIframe.setAttribute('src', '');
      }
    }
  }
  
  // Create a global instance
  window.productCardManager = new ProductCardManager();
  
  // Initialize on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', function() {
    window.productCardManager.init('[data-product-card]');
  });